import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/config/api';
import { 
  ArrowRight,
  ChevronDown,
  Building2,
  UserCircle,
  ShoppingBag,
  MessageCircle,
  Mail,
  Phone,
  HelpCircle,
  Search,
  ExternalLink,
  Clock,
  CheckCircle,
  Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SUPPORT_PHONE, SUPPORT_PHONE_TEL } from '@/config/support';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { toast } from 'sonner';

import { ASTERPOS_LOGO } from '@/config/brandAssets';

export default function SupportPage() {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    category: '',
    message: ''
  });
  const [sending, setSending] = useState(false);

  // Only channels that answer: the support line, e-mail and the message form below. (Live chat
  // and a knowledge base / video / docs library were listed with buttons that did nothing.)
  const supportOptions = [
    {
      icon: Phone,
      title: 'Call Support',
      description: SUPPORT_PHONE,
      availability: 'Mon-Fri, 9am-6pm PST',
      action: 'Call now',
      href: SUPPORT_PHONE_TEL,
      color: 'from-orange-500 to-orange-600'
    },
    {
      icon: Mail,
      title: 'Email Support',
      description: 'Send us a detailed message',
      availability: 'Response within 24 hours',
      action: 'Send Email',
      href: 'mailto:support@asterpos.com',
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: MessageCircle,
      title: 'Message Us',
      description: 'Use the form on this page',
      availability: 'We reply by e-mail',
      action: 'Write a message',
      href: '#contact-form',
      color: 'from-blue-500 to-blue-600'
    }
  ];

  const resources = [
    {
      icon: HelpCircle,
      title: 'FAQ',
      description: 'Common questions answered',
      link: '#faq'
    }
  ];

  const faqs = [
    {
      category: 'Getting Started',
      questions: [
        {
          q: 'How do I set up my restaurant on Chain29?',
          a: 'Sign up for a free trial, follow the onboarding wizard to add your restaurant details, import your menu, and connect your payment terminal. Our setup guide walks you through each step.'
        },
        {
          q: 'What devices can I use with Chain29?',
          a: 'Chain29 works on any device with a modern web browser - iPads, Android tablets, Windows PCs, Macs, and Chromebooks. We recommend iPad or Android tablets for the best experience.'
        },
        {
          q: 'How long does setup take?',
          a: 'Most restaurants are up and running within 30 minutes. Menu import, basic setup, and payment configuration can all be done in under an hour.'
        }
      ]
    },
    {
      category: 'Billing & Payments',
      questions: [
        {
          q: 'How does the free trial work?',
          a: 'You get 7 days free on the plan you pick. You add a billing card when you sign up; autopay charges it one day before the trial ends, and you can change plans or cancel before then.'
        },
        {
          q: 'Can I change my plan later?',
          a: 'Yes! You can upgrade or downgrade your plan at any time from your account settings. Changes take effect on your next billing cycle.'
        },
        {
          q: 'What are the payment processing fees?',
          a: 'We charge 2.6% + 10¢ per card transaction. There are no hidden fees, monthly minimums, or PCI compliance charges.'
        }
      ]
    },
    {
      category: 'Technical',
      questions: [
        {
          q: 'Does Chain29 work offline?',
          a: 'Yes! The POS has offline mode that stores transactions locally and syncs when internet is restored. Your business keeps running even without connectivity.'
        },
        {
          q: 'How do I connect a receipt printer?',
          a: 'Chain29 supports Star, Epson, and most network/USB receipt printers. Go to Settings > Hardware > Add Printer and follow the setup wizard.'
        },
        {
          q: 'Can I integrate with third-party apps?',
          a: 'Yes! We integrate with DoorDash, UberEats, Grubhub, QuickBooks, 7shifts, and many more. Check our integrations page for the full list.'
        }
      ]
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.email || !contactForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSending(true);
    try {
      const subject = contactForm.subject || contactForm.category || 'Support request';
      const response = await axios.post(`${API}/site/contact`, {
        name: contactForm.name || contactForm.email,
        email: contactForm.email,
        subject: contactForm.category && contactForm.subject ? `[${contactForm.category}] ${subject}` : subject,
        message: contactForm.message,
        source_page: 'support'
      });
      toast.success(response.data?.message || 'Message sent!');
      setContactForm({
        name: '',
        email: '',
        subject: '',
        category: '',
        message: ''
      });
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not send your message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Navigation */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-black/40 backdrop-blur-md">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={ASTERPOS_LOGO} alt="Chain29" className="h-8 w-8" />
              <span className="text-xl font-bold text-white">Chain29</span>
            </Link>

            <nav className="hidden md:flex items-center gap-6">
              <Link to="/product" className="text-slate-300 hover:text-white text-sm font-medium">
                Product
              </Link>
              <Link to="/pricing" className="text-slate-300 hover:text-white text-sm font-medium">
                Pricing
              </Link>
              <Link to="/support" className="text-white text-sm font-medium">
                Support
              </Link>
            </nav>

            <div className="flex items-center gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="text-white hover:text-white hover:bg-white/10">
                    Login
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem asChild>
                    <Link to="/owner/login" className="flex items-center gap-2 cursor-pointer">
                      <Building2 className="w-4 h-4" />
                      <div>
                        <div className="font-medium">Restaurant Owner</div>
                        <div className="text-xs text-muted-foreground">Manage your restaurant</div>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/staff/login" className="flex items-center gap-2 cursor-pointer">
                      <UserCircle className="w-4 h-4" />
                      <div>
                        <div className="font-medium">Employee / Staff</div>
                        <div className="text-xs text-muted-foreground">Clock in & access POS</div>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/auth" className="flex items-center gap-2 cursor-pointer">
                      <ShoppingBag className="w-4 h-4" />
                      <div>
                        <div className="font-medium">Customer</div>
                        <div className="text-xs text-muted-foreground">Order food online</div>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Link to="/owner/signup">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white">
                  Start Free Trial
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            How Can We Help?
          </h1>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Get the support you need to run your restaurant smoothly.
          </p>
          
          {/* Search Box */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input 
              placeholder="Search for help articles..." 
              className="pl-12 py-6 bg-white/10 border-white/20 text-white placeholder:text-slate-400"
            />
          </div>
        </div>
      </section>

      {/* Support Options */}
      <section className="pb-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {supportOptions.map((option) => {
              const Icon = option.icon;
              return (
                <Card key={option.title} className="bg-white/5 border-white/10 text-center hover:border-white/20 transition-colors">
                  <CardContent className="pt-8 pb-6">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${option.color} flex items-center justify-center mx-auto mb-4`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{option.title}</h3>
                    <p className="text-slate-300 mb-2">{option.description}</p>
                    <p className="text-sm text-slate-400 mb-4 flex items-center justify-center gap-1">
                      <Clock className="w-4 h-4" />
                      {option.availability}
                    </p>
                    <Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/10">
                      <a href={option.href}>{option.action}</a>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Resources */}
      <section className="py-16 border-t border-white/10">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Self-Help Resources</h2>
          <div className="grid gap-6 max-w-sm mx-auto">
            {resources.map((resource) => {
              const Icon = resource.icon;
              return (
                <a 
                  key={resource.title}
                  href={resource.link}
                  className="bg-white/5 border border-white/10 rounded-lg p-6 hover:border-white/20 transition-colors group"
                >
                  <Icon className="w-8 h-8 text-orange-400 mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-1 group-hover:text-orange-400 transition-colors">
                    {resource.title}
                  </h3>
                  <p className="text-sm text-slate-400">{resource.description}</p>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 border-t border-white/10">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-white text-center mb-4">Frequently Asked Questions</h2>
          <p className="text-slate-300 text-center mb-12">Find answers to common questions</p>
          
          {faqs.map((category) => (
            <div key={category.category} className="mb-8">
              <h3 className="text-lg font-semibold text-orange-400 mb-4">{category.category}</h3>
              <Accordion type="single" collapsible className="space-y-3">
                {category.questions.map((faq, idx) => (
                  <AccordionItem 
                    key={idx} 
                    value={`${category.category}-${idx}`}
                    className="bg-white/5 border border-white/10 rounded-lg px-6"
                  >
                    <AccordionTrigger className="text-white hover:no-underline text-left">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-300">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="py-16 border-t border-white/10">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Still Need Help?</h2>
              <p className="text-slate-300">Send us a message and we'll get back to you within 24 hours</p>
            </div>
            
            <Card className="bg-white/5 border-white/10">
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white">Name</Label>
                      <Input 
                        value={contactForm.name}
                        onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                        placeholder="Your name"
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white">Email *</Label>
                      <Input 
                        type="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                        placeholder="your@email.com"
                        className="bg-white/10 border-white/20 text-white"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white">Subject</Label>
                      <Input 
                        value={contactForm.subject}
                        onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                        placeholder="Brief description"
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white">Category</Label>
                      <Select 
                        value={contactForm.category} 
                        onValueChange={(v) => setContactForm({...contactForm, category: v})}
                      >
                        <SelectTrigger className="bg-white/10 border-white/20 text-white">
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="technical">Technical Issue</SelectItem>
                          <SelectItem value="billing">Billing Question</SelectItem>
                          <SelectItem value="feature">Feature Request</SelectItem>
                          <SelectItem value="general">General Inquiry</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-white">Message *</Label>
                    <Textarea 
                      value={contactForm.message}
                      onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                      placeholder="Describe your issue or question in detail..."
                      rows={5}
                      className="bg-white/10 border-white/20 text-white resize-none"
                      required
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-orange-500 hover:bg-orange-600"
                    disabled={sending}
                  >
                    {sending ? (
                      <>Sending...</>
                    ) : (
                      <>
                        Send Message
                        <Send className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src={ASTERPOS_LOGO} alt="Chain29" className="h-6 w-6" />
              <span className="text-white font-semibold">Chain29</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-400">
              <Link to="/product" className="hover:text-white">Product</Link>
              <Link to="/pricing" className="hover:text-white">Pricing</Link>
              <Link to="/about" className="hover:text-white">About</Link>
              <Link to="/contact" className="hover:text-white">Contact</Link>
              <Link to="/terms" className="hover:text-white">Terms</Link>
              <Link to="/privacy" className="hover:text-white">Privacy</Link>
            </div>
            <p className="text-sm text-slate-500">© 2026 Chain29. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
